/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, UseFieldArrayProps, useWatch } from 'react-hook-form';

export type FormAddOptions<TFormItem, TItem,> = Pick<UseFieldArrayProps<any, any>, 'control' | 'name'> & {
  /**
   * Map a `TItem` item into a `TFormItem` - This method will be called as part of the `setNewItems` method
   * @param item
   * @returns Mapped `TFormItem` item
   */
  mapItemToFormItem: (item: TItem) => TFormItem;
  /**
   * Check if a `TItem` item equals a `TFormItem` item - This method will be called as part of the methods `removeFormItem` & `getItemOfFormItem`
   * @param item
   * @param formItem
   * @returns Will be true if a `TItem` item is the respective item of a `TFormItem` item
   */
  itemEqualsFormItem: (item: TItem, formItem: TFormItem) => boolean;
  /**
   * Check if a `TFormItem` item equals another `TFormItem` item - This method will be called as part of the methods `setNewItems` & `removeFormItem`
   * @param formItem
   * @param otherFormItem
   * @returns Will be true if the `TFormItem` item is the same as the other `TFormItem`item
   */
  formItemEqualsFormItem: (formItem: TFormItem, otherFormItem: TFormItem) => boolean;
  /**
   * Optional: Callback function which will be called as part of the `enableAdding` method
   */
  onEnableAdding?: () => void;
  /**
   * Optional: Callback function which will be called as part of the `disableAdding` method
   */
  onDisableAdding?: () => void;
  /**
   * Optional: Provide a preselected item that will be added to the `items` & `formItems` prop correctly -
   * Needs to have `isFetchingPreselectedItem` & `isFetchingPreselectedItemSuccessful` set in order to work properly
   */
  preselectedItem?: TItem;
  /**
   * Optional: Flag that determines that the `preselectedItem` is currently being fetched
   */
  isFetchingPreselectedItem?: boolean;
  /**
   * Optional: Flag that determines that the `preselectedItem` has been successfully fetched
   */
  isFetchingPreselectedItemSuccessful?: boolean;
}

/**
 * A hook that extracts the functionality of having an add dialog that will provide a bunch of entities to the form which will be then feed
 * into the form (and therefore into this hook) and provides the handling of adding those entities to the form, removing form items and a bunch of other reusable methods in that context
 * @returns
 */
export const useFormAdd = <TFormItem, TItem>({
  control,
  name,
  mapItemToFormItem,
  itemEqualsFormItem,
  formItemEqualsFormItem,
  onEnableAdding,
  onDisableAdding,
  preselectedItem,
  isFetchingPreselectedItem,
  isFetchingPreselectedItemSuccessful
}: FormAddOptions<TFormItem, TItem>) => {

  // Don't use `fields` prop to access data as it's suggested to access this data inside fields via `useWatch` instead
  // MUST use if going to iterate over a list of entries (see: https://react-hook-form.com/docs/usefieldarray -> Rules -> field.id)
  // Also fields prop will contain a random generated `id` prop provided by `react-hook-form` which would replace `id` prop if existing inside this form value
  const { fields, append, remove } = useFieldArray({ control, name });

  /**
   * List of `formItem` which is coming from the `react-hook-form` via `useWatch` - Its being used here to reference the correct form item with a non overwritten `id` -> see `fields` prop comment on top
   */
  const formItems: TFormItem[] = useWatch({ control, name: name as any, defaultValue: [] });

  /**
   * List of items to have a reference for each `formItem` (e. g. if you need more meta information about a `formItem`)
   */
  const [items, setItems] = useState<TItem[]>([]);

  /**
   * A value which can be used to determine if `adding` process is currently enabled or not
   */
  const [adding, setAdding] = useState<boolean>(false);

  /**
   * Set `adding` prop to `true` and call the callback `onEnableAdding`
   */
  const enableAdding = () => {
    setAdding(true);
    if (typeof onEnableAdding === 'function') {
      onEnableAdding();
    }
  };

  /**
   * Set `adding` prop to `false` and call the callback `onDisableAdding`
   */
  const disableAdding = () => {
    setAdding(false);
    if (typeof onDisableAdding === 'function') {
      onDisableAdding();
    }
  };

  /**
   * Set (replace) `items` prop with the provided `newItems` and also set (replace) `formItems` with the help of `mapItemToFormItem` method -
   * It will consider currently set `formItems` and therfore won't replace the values inside of each `formItem` in the `formItems`
   * @param newItems
   */
  const setNewItems = (newItems: TItem[]) => {
    setItems(() => newItems);

    const newFormItems = newItems.map<TFormItem>(mapItemToFormItem)

    // keep current form items with given values if it was added again
    formItems.forEach((currentFormItem) => {
      const idx = newFormItems.findIndex((newFormItem) => formItemEqualsFormItem(currentFormItem, newFormItem))
      if (idx <= -1) return;
      newFormItems[idx] = currentFormItem;
    });

    remove(); // we have to first remove all entries (=> clear) otherwise it will create duplicates
    append([...newFormItems], { shouldFocus: false });
    setAdding(false);
  };

  /**
   * Append new `formItems` to the `formItems` prop
   * Those `formItems` won't have a reference to a `item`
   * They also get overwritten / removed when `setNewItems` is called
   * @param newFormItems
   */
  const appendNewFormItems = (newFormItems: TFormItem[]) => {
    append([...newFormItems], { shouldFocus: false });
  }

  /**
   * Remove the an `item` from the `items` prop and its respective `formItem` variant in the `formItems` prop by using the `fields` provided index
   * @param fieldIndex Index of the input inside the `fields` array
   */
  const removeItemByFieldIndex = (fieldIndex: number) => {
    const formItem = formItems[fieldIndex];

    if (formItem == null) return;

    if (fieldIndex >= 0) {
      remove(fieldIndex)
    }

    const itemIndex = items.findIndex((x) => itemEqualsFormItem(x, formItem));
    if (itemIndex >= 0) {
      const newSpaces = [...items];
      newSpaces.splice(itemIndex, 1);
      setItems(() => newSpaces);
    }
  }

  /**
   * Util method to get the respective `item` of a given `formItem` if exists by using the index of the `fields` prop
   * @param fieldIndex
   */
  const getItemByFieldIndex = useCallback((fieldIndex: number) => {
    const formItem = formItems[fieldIndex];

    if (formItem == null) return;

    return items.find((x) => itemEqualsFormItem(x, formItem))
  }, [formItems, itemEqualsFormItem, items]);

  /**
   * Util method to get the `formItem` by using the index of the `fields` prop
   * @param fieldIndex
   */
  const getFormItemByFieldIndex = useCallback((fieldIndex: number) => {
    const formItem = formItems[fieldIndex];
    if (formItem == null) return;
    return formItem;
  }, [formItems]);

  /**
   * Boolean flag which will be `true` if `items` prop is a non empty array otherwise it will be `false`
   */
  const hasItems = useMemo(() => Array.isArray(items) && items.length > 0, [items]);

  /**
   * Number of `items` - If `items` is nullable or empty then it will return `undefined`
   */
  const itemCount = useMemo(() => items != null && items.length > 0 ? items.length : undefined, [items]);

  /**
   * Set adding to false whenever a preselected item is fetched (makes sure that adding wont be active as long as fetching changes)
   */
  useEffect(() => {
    setAdding(false);
  }, [isFetchingPreselectedItem]);

  /**
   * Add preselectedItem whenever fetching of the item is successful
   */
  useEffect(() => {
    if (preselectedItem == null) return;
    setNewItems([preselectedItem]);
  }, [isFetchingPreselectedItemSuccessful]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    fields,
    items,
    setItems,
    adding,
    enableAdding,
    disableAdding,
    setNewItems,
    appendNewFormItems,
    removeItemByFieldIndex,
    getItemByFieldIndex,
    getFormItemByFieldIndex,
    hasItems,
    itemCount
  }
};
